import { BIOME_IDS, BIOMES, type BiomeId } from '@shefira/shared';

import './village-sign.css';

/**
 * The village's sign, shown in the middle of the screen when the player
 * walks in or lands at its portal: its name, in its biome's own material
 * (`ui/sign-<biome>.png`, one per village). A welcome, nothing to click:
 * the sign lets every click through to the world, and fades on its own.
 *
 * Drawn at a whole multiple of its pixels (CLAUDE.md rule 6), the largest
 * that fits the window up to three.
 */

const SIGN_WIDTH = 168;
const SIGN_HEIGHT = 64;
const MAX_SCALE = 3;
const HOLD_MS = 2600;
const FADE_MS = 500;
/**
 * A player pacing along a village's edge would call it up every few steps:
 * the same sign waits this long before it shows again.
 */
const AGAIN_MS = 60_000;

function biomeOfVillage(name: string): BiomeId | undefined {
  return BIOME_IDS.find((id) => BIOMES[id].village === name);
}

export class VillageSign {
  private readonly root: HTMLElement;
  private readonly image: HTMLImageElement;
  private readonly shownAt = new Map<string, number>();
  private timers: number[] = [];

  constructor() {
    this.root = document.createElement('div');
    this.root.className = 'village-sign';
    this.root.hidden = true;
    this.root.setAttribute('aria-live', 'polite');
    this.image = document.createElement('img');
    this.image.className = 'village-sign__image';
    this.image.width = SIGN_WIDTH;
    this.root.append(this.image);
    document.body.append(this.root);
  }

  /** Show the sign of a village, after `delayMs` (the teleport's flames part first). */
  show(village: string, delayMs = 0): void {
    const biome = biomeOfVillage(village);
    if (biome === undefined) return;
    const now = performance.now();
    const last = this.shownAt.get(village);
    if (last !== undefined && now - last < AGAIN_MS) return;
    this.shownAt.set(village, now);

    this.clearTimers();
    this.root.hidden = true;
    this.root.classList.remove('village-sign--shown');
    this.image.src = `/assets/ui/sign-${biome}.png`;
    this.image.alt = village;
    this.later(delayMs, () => {
      // Bounded by the height too: a phone on its side is wide but short, and
      // a 3x sign would hide the middle of the screen, the player included.
      const scale = Math.max(
        1,
        Math.min(
          MAX_SCALE,
          Math.floor((window.innerWidth * 0.8) / SIGN_WIDTH),
          Math.floor((window.innerHeight * 0.4) / SIGN_HEIGHT),
        ),
      );
      this.image.style.width = `${String(SIGN_WIDTH * scale)}px`;
      this.root.hidden = false;
      // One frame hidden-then-shown, so the fade has a start to run from.
      this.later(20, () => {
        this.root.classList.add('village-sign--shown');
      });
      this.later(HOLD_MS, () => {
        this.root.classList.remove('village-sign--shown');
      });
      this.later(HOLD_MS + FADE_MS, () => {
        this.root.hidden = true;
      });
    });
  }

  destroy(): void {
    this.clearTimers();
    this.root.remove();
  }

  private later(ms: number, run: () => void): void {
    this.timers.push(window.setTimeout(run, ms));
  }

  private clearTimers(): void {
    for (const timer of this.timers) window.clearTimeout(timer);
    this.timers = [];
  }
}
